// Helper: ask the server to refund a credit for a tryon that the client
// could not finish (image generated server-side but client failed to
// persist, or network died after spendCredit). Server enforces:
//   - row belongs to user
//   - row is not `complete`
//   - no prior refund exists for this tryon_id (idempotent)
import { supabase } from "@/integrations/supabase/client";

export async function requestTryonRefund(tryonId: string, reason = "refund:client_failure") {
  try {
    // Long-running generations can outlive the access token. Proactively
    // refresh so the edge function sees a valid JWT instead of 401-ing.
    const { data: sessionData } = await supabase.auth.getSession();
    let accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      // Not signed in — nothing we can refund client-side. Silent no-op.
      return { refunded: false };
    }
    const expiresAt = sessionData.session?.expires_at ?? 0;
    const nowSec = Math.floor(Date.now() / 1000);
    if (expiresAt - nowSec < 60) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed.session?.access_token) {
        accessToken = refreshed.session.access_token;
      }
    }

    // Use explicit fetch instead of supabase.functions.invoke() so the
    // Authorization header is guaranteed to be set with a fresh token.
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refund-failed-tryon`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({ tryonId, reason }),
    });

    if (!response.ok) {
      // 401 means the session is genuinely gone. Refund is best-effort —
      // don't spam the console.
      if (response.status !== 401) {
        console.warn("[refund-tryon] failed", response.status, await response.text());
      }
      return { refunded: false };
    }
    return (await response.json()) as { refunded: boolean; balance?: number };
  } catch (e) {
    console.warn("[refund-tryon] threw", e);
    return { refunded: false };
  }
}
